'use client';

/**
 * A/B TESTS ONE-FOCUS DECK — tests are the cards, aggregated across every
 * funnel (/api/funnels → [id]/stats → stats['ab-tests'].tests). Focus:
 * WHY = assignments/description, PREVIEW = variants side-by-side with real
 * rates, DECIDE = winner state + funnel admin deep-link (no promote API
 * exists — honest parity, the classic page has none either).
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, CY, stColor, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

function Why({ item }: { item: OFItem }) {
  const t = item.raw;
  return (
    <>
      {[['FUNNEL', t._funnelName], ['PAGE', t.pagePath], ['STATUS', t.status?.toUpperCase()], ['ASSIGNMENTS', String(t.totalAssignments ?? 0)], ['VARIANTS', String(t.variants?.length ?? 0)]].map(([k, v]) => (
        <div key={k as string} className="flex justify-between" style={{ padding: '4px 0', gap: 10 }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd', flexShrink: 0 }}>{k}</span>
          <span style={{ ...mono(16, '.04em'), color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v as string}</span>
        </div>
      ))}
      {t.description && (
        <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
          <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 6 }}>HYPOTHESIS</div>
          <div style={{ ...display(24, 300), color: 'var(--uif-t-body)', lineHeight: 1.65 }}>{t.description}</div>
        </div>
      )}
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const t = item.raw;
  const best = Math.max(...(t.variants || []).map((x: any) => x.conversionRate || 0), 0.0001);
  return (
    <>
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 12 }}>VARIANTS · SIDE BY SIDE · REAL RATES</div>
      {(t.variants || []).map((v: any) => {
        const isWinner = t.winnerVariantId === v.variantId;
        const lead = (v.conversionRate || 0) >= best && best > 0.0001;
        return (
          <div key={v.variantId} style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 10, border: `1px solid ${isWinner || lead ? 'rgba(93,242,180,.4)' : 'rgba(125,232,255,.14)'}`, background: isWinner || lead ? 'rgba(93,242,180,.04)' : 'rgba(8,18,28,.4)' }}>
            <div className="flex items-center" style={{ gap: 10 }}>
              <span style={{ ...display(30, 400), color: isWinner || lead ? '#5df2b4' : '#e9f9ff' }}>{v.variantKey}</span>
              <span style={{ ...display(24, 300), color: '#c4d8e4', flex: 1 }}>{v.variantName}{v.isControl ? ' · CONTROL' : ''}</span>
              {isWinner && <span style={{ ...mono(13, '.18em'), color: '#5df2b4', border: '1px solid rgba(93,242,180,.5)', borderRadius: 999, padding: '2px 8px' }}>WINNER</span>}
              {!isWinner && lead && <span style={{ ...mono(13, '.18em'), color: '#9ef0ff', border: '1px solid rgba(158,240,255,.4)', borderRadius: 999, padding: '2px 8px' }}>LEADING</span>}
            </div>
            <div className="flex items-baseline" style={{ gap: 14, marginTop: 8 }}>
              <span style={{ fontWeight: 200, fontSize: 48, color: '#aef0ff', textShadow: '0 0 14px rgba(0,240,255,.3)' }}>{v.conversionRate}%</span>
              <span style={{ ...mono(16), color: '#7fa9bd' }}>{v.assignments} → {v.conversions}</span>
            </div>
            <div style={{ height: 3, marginTop: 8, width: `${Math.max(2, (v.conversionRate || 0) / best * 100)}%`, background: isWinner || lead ? 'linear-gradient(90deg,#5df2b4,rgba(93,242,180,.1))' : 'linear-gradient(90deg,#9ef0ff,rgba(125,232,255,.06))' }} />
          </div>
        );
      })}
    </>
  );
}

function Decide({ item }: { item: OFItem }) {
  const t = item.raw;
  const winner = (t.variants || []).find((v: any) => v.variantId === t.winnerVariantId);
  const sample = t.totalAssignments || 0;
  return (
    <>
      <div style={{ ...mono(14, '.24em'), color: '#49677a', marginBottom: 8 }}>SAMPLE</div>
      <div style={{ fontWeight: 200, fontSize: 52, color: sample >= 100 ? '#5df2b4' : '#ffc36b', marginBottom: 4 }}>{sample}</div>
      <div style={{ ...mono(13, '.14em'), color: '#56788c', marginBottom: 16 }}>{sample >= 100 ? 'SAMPLE SUFFICIENT FOR A CALL' : 'STILL COLLECTING — SMALL SAMPLE'}</div>
      {winner ? (
        <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: '1px solid rgba(93,242,180,.5)', color: '#5df2b4', ...mono(18, '.26em') }}>WINNER · {winner.variantName?.toUpperCase()}</div>
      ) : (
        <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: `1px solid color-mix(in srgb, ${stColor(t.status)} 40%, transparent)`, color: stColor(t.status), ...mono(18, '.26em') }}>{(t.status || '').toUpperCase()} · NO WINNER YET</div>
      )}
      <a href={`/funnels/ab-tests`} style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 12, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>CLASSIC A/B SUITE ↗</div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>WINNER PROMOTION LIVES IN THE FUNNEL ADMIN</div>
    </>
  );
}

export function AbDeckSurface({ funnelId }: { funnelId?: string | null }) {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(async () => {
    try {
      const d = await fetch('/api/funnels').then(r => r.json());
      const funnels: any[] = (d.funnels || []).filter((f: any) => !funnelId || f.id === funnelId);
      const all: OFItem[] = [];
      await Promise.all(funnels.map(async f => {
        try {
          const s = await fetch(`/api/funnels/${f.id}/stats`).then(r => r.json());
          for (const t of s?.stats?.['ab-tests']?.tests || []) {
            const best = [...(t.variants || [])].sort((a: any, b: any) => (b.conversionRate || 0) - (a.conversionRate || 0))[0];
            all.push({
              id: `${f.id}:${t.id}`, biz: f.name, title: t.testName, type: 'A/B TEST',
              status: t.winnerVariantId ? 'promoted' : t.status,
              kpis: [
                { l: 'ASSIGNMENTS', v: String(t.totalAssignments ?? 0) },
                { l: 'VARIANTS', v: String(t.variants?.length ?? 0) },
                { l: 'BEST', v: best ? `${best.conversionRate}%` : '—' },
              ],
              taste: t.description || t.pagePath, foot: t.pagePath,
              raw: { ...t, _funnelId: f.id, _funnelName: f.name },
            });
          }
        } catch { /* funnel unreachable — skip honestly */ }
      }));
      setItems(all); setLoaded(true);
    } catch { setLoaded(true); }
  }, [funnelId]);
  useEffect(() => { load(); }, [load]);

  const adapter: OFAdapter = {
    kind: 'ab',
    strap: (its) => `ONE FOCUS · ${its.length} LIVE SPLITS · REAL RATES`,
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
